import moment from "moment";
import {
  RollEntry,
  RollSessionFieldsFragment,
  WeekRollFieldsFragment,
} from "../../../../../generated/graphql";

export const ROLL_DATE_FORMAT = "YYYY-MM-DD";
export const ROLL_TIME_FORMAT = "HH:mm";

// Sessions may only differ from the class length in steps of this many minutes.
// Make sure this is in sync with the service.
export const SESSION_DURATION_STEP_MINUTES = 30;
const MAX_EXTRA_SESSION_MINUTES = 4 * 60;

// Make sure this is in sync with the service.
export const DEFAULT_COMMENT_ONLINE = "Student attended online";
export const DEFAULT_COMMENT_STUDENT_ABSENT_WITHOUT_NOTICE =
  "Student absent without notice";
export const DEFAULT_COMMENT_STUDENT_ABSENT_WITH_NOTICE_SICK =
  "Student absent with notice - Sick or isolating";
export const DEFAULT_COMMENT_STUDENT_ABSENT_WITH_NOTICE_OTHER =
  "Student absent with notice - Other reason";
export const DEFAULT_COMMENT_TEACHER_ABSENT_SICK =
  "Class cancelled due to teacher absent - Sick or isolating";
export const DEFAULT_COMMENT_TEACHER_ABSENT_OTHER =
  "Class cancelled due to teacher absent - Other reason";
export const ABSENT_COMMENTS = [
  DEFAULT_COMMENT_STUDENT_ABSENT_WITHOUT_NOTICE,
  DEFAULT_COMMENT_STUDENT_ABSENT_WITH_NOTICE_SICK,
  DEFAULT_COMMENT_STUDENT_ABSENT_WITH_NOTICE_OTHER,
  DEFAULT_COMMENT_TEACHER_ABSENT_SICK,
  DEFAULT_COMMENT_TEACHER_ABSENT_OTHER,
];
export const COMMENT_PRESETS = [DEFAULT_COMMENT_ONLINE, ...ABSENT_COMMENTS];

/** Students are charged a lesson for comments containing this, so it can only be used for absent students. */
export const CHARGEABLE_ABSENCE_PATTERN = /without/i;

export const isAbsentComment = (comment: string): boolean =>
  ABSENT_COMMENTS.includes(comment.trim());

/**
 * Comment of a student who was present which is not allowed (as it implies they were absent).
 */
export const isInvalidPresentComment = (comment: string): boolean =>
  isAbsentComment(comment) || CHARGEABLE_ABSENCE_PATTERN.test(comment);

export const getRollEntry = (
  weekRoll: WeekRollFieldsFragment,
  sessionKey: string,
  attendanceId: number
): RollEntry | undefined =>
  weekRoll.entries.find(
    (entry) =>
      entry.sessionKey === sessionKey && entry.attendanceId === attendanceId
  );

export const isFutureSession = (
  session: Pick<RollSessionFieldsFragment, "date">
): boolean => moment(session.date, ROLL_DATE_FORMAT).isAfter(moment(), "day");

/** e.g. "27/04" */
export const formatSessionShortDate = (
  session: Pick<RollSessionFieldsFragment, "date">
): string => moment(session.date, ROLL_DATE_FORMAT).format("DD/MM");

/** e.g. "Wed 27/04" */
export const formatSessionDate = (
  session: Pick<RollSessionFieldsFragment, "date">
): string => moment(session.date, ROLL_DATE_FORMAT).format("ddd DD/MM");

/** e.g. "4:00 pm - 6:00 pm" */
export const formatSessionTimes = (
  session: Pick<RollSessionFieldsFragment, "startTime" | "endTime">
): string =>
  `${moment(session.startTime, ROLL_TIME_FORMAT).format("h:mm a")} - ${moment(
    session.endTime,
    ROLL_TIME_FORMAT
  ).format("h:mm a")}`;

/** e.g. "Wed 27/04 4:00 pm - 6:00 pm" */
export const formatSession = (
  session: Pick<RollSessionFieldsFragment, "date" | "startTime" | "endTime">
): string => `${formatSessionDate(session)} ${formatSessionTimes(session)}`;

/** e.g. "1h 30m" */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return [
    hours > 0 ? `${hours}h` : "",
    remainingMinutes > 0 ? `${remainingMinutes}m` : "",
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * Durations a session can have, which is the class length plus or minus multiples of 30 minutes.
 */
export const getSessionDurationOptions = (classMinutes: number): number[] => {
  const options = [];
  const minimum = ((classMinutes - 1) % SESSION_DURATION_STEP_MINUTES) + 1;
  for (
    let duration = minimum;
    duration <= classMinutes + MAX_EXTRA_SESSION_MINUTES;
    duration += SESSION_DURATION_STEP_MINUTES
  ) {
    options.push(duration);
  }
  return options;
};
